export const hasNfcEnabled = (nfcRaw: unknown): boolean => {
  if (typeof nfcRaw === 'string') return nfcRaw.length > 0;
  return !!(nfcRaw && typeof nfcRaw === 'object' && (nfcRaw as { enabled?: boolean }).enabled);
};

export const truncate = (str: string | undefined, max: number): string | undefined => {
  if (!str) return str;
  return str.length > max ? str.slice(0, max) + '...' : str;
};

// ---- Timezone utilities ----

export interface TimezoneOption {
  label: string;
  value: string;
  offsetMinutes: number;
}

const getTzNamePart = (
  tz: string,
  date: Date,
  locale: string,
  style: 'short' | 'long' | 'shortOffset'
): string => {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    timeZoneName: style,
  }).formatToParts(date);
  return parts.find((p) => p.type === 'timeZoneName')?.value || '';
};

/** Get a human-readable timezone abbreviation (e.g. BST, GMT, EST, JST) for a given date. */
export const getTimezoneAbbr = (tz: string, date?: Date): string => {
  const d = date || new Date();

  for (const locale of ['en-GB', 'en-US', 'en']) {
    const val = getTzNamePart(tz, d, locale, 'short');
    if (val && !val.startsWith('GMT+') && !val.startsWith('GMT-')) return val;
  }

  const longName = getTzNamePart(tz, d, 'en-GB', 'long');
  if (longName) {
    const abbr = longName.split(' ').map((w) => w[0]).join('').toUpperCase();
    if (abbr.length >= 2 && abbr.length <= 5) return abbr;
  }

  return getTzNamePart(tz, d, 'en-US', 'shortOffset') || 'GMT';
};

const MAJOR_CITIES = new Set([
  'Pacific/Midway', 'Pacific/Honolulu', 'America/Anchorage', 'America/Los_Angeles',
  'America/Denver', 'America/Chicago', 'America/New_York', 'America/Halifax',
  'America/St_Johns', 'America/Sao_Paulo', 'America/Argentina/Buenos_Aires',
  'America/Mexico_City', 'America/Bogota', 'America/Lima', 'America/Caracas',
  'Atlantic/Azores', 'Atlantic/Cape_Verde',
  'Europe/London', 'Europe/Dublin', 'Europe/Lisbon',
  'Europe/Paris', 'Europe/Berlin', 'Europe/Amsterdam', 'Europe/Brussels',
  'Europe/Madrid', 'Europe/Rome', 'Europe/Zurich', 'Europe/Vienna',
  'Europe/Warsaw', 'Europe/Prague', 'Europe/Stockholm', 'Europe/Oslo',
  'Europe/Copenhagen', 'Europe/Helsinki', 'Europe/Bucharest',
  'Europe/Athens', 'Europe/Istanbul', 'Europe/Moscow',
  'Africa/Casablanca', 'Africa/Cairo', 'Africa/Nairobi',
  'Africa/Johannesburg', 'Africa/Lagos',
  'Asia/Dubai', 'Asia/Muscat', 'Asia/Karachi', 'Asia/Kolkata',
  'Asia/Dhaka', 'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Singapore',
  'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Taipei', 'Asia/Seoul',
  'Asia/Tokyo', 'Asia/Riyadh', 'Asia/Tehran', 'Asia/Kabul',
  'Australia/Perth', 'Australia/Adelaide', 'Australia/Sydney',
  'Australia/Brisbane', 'Australia/Melbourne',
  'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Guam',
]);

const parseGmtOffset = (offsetStr: string): number => {
  if (offsetStr === 'GMT') return 0;
  const match = offsetStr.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3] || '0', 10));
};

/** Build timezone dropdown options with dynamic abbreviations based on a target date. */
export const buildTimezoneOptions = (date?: Date): TimezoneOption[] => {
  const d = date || new Date();
  const zones = Intl.supportedValuesOf('timeZone').filter((tz) => MAJOR_CITIES.has(tz));

  return zones
    .map((tz) => {
      const offsetStr = getTzNamePart(tz, d, 'en-US', 'shortOffset') || 'GMT';
      const city = tz.split('/').pop()?.replace(/_/g, ' ') || tz;
      const abbr = getTimezoneAbbr(tz, d);

      return {
        label: `(${abbr}) ${city}`,
        value: tz,
        offsetMinutes: parseGmtOffset(offsetStr),
      };
    })
    .sort((a, b) => a.offsetMinutes - b.offsetMinutes);
};
